import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import {
   MedicinePrescription,
   MedicinePrescriptionQuery,
   PatientMedicine,
} from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { useCqrsGet } from '@dreryk/command-query-react-common';
import { commonTranslationKeys } from '@dreryk/react-common';
import { LoaderSpinner, Message, MessageType } from '@dreryk/react-components';
import { Choice, Slot } from '@dreryk/watermelon';

import { useGetPatientEntitlementToMedicalServiceStatus } from '~libs.shared.patient-entitlement-to-medical-service';
import { VisitId } from '~libs.shared.visit';
import { MedicinePackage } from '~libs.desktop.shared.medicine';

import { PatientId } from '../../../PrescriptionIssuingPanel.types';
import { MedicineHeading } from '../../prescription/MedicineHeading';
import { Quantity } from '../../prescription/quantity/quantity.types';
import { DraftPrescriptionCardSkeleton } from '../common/DraftPrescriptionCard.skeleton';

type DraftStandardPrescriptionFieldLoaderRenerProps = {
   medicinePrescription: MedicinePrescription;
};

type DraftStandardPrescriptionFieldLoaderProps = {
   readonly visitId: VisitId;
   readonly patientId: PatientId;
   readonly medicineId: MedicineSimple['id'];
   readonly initialData: MedicinePrescription | undefined;
   readonly medicinePackageQuantity: Quantity;
   readonly patientMedicine: PatientMedicine | undefined;
   readonly visitDiagnoses: VisitDiagnosis[];
   readonly index: number;
   readonly children: (renderProps: DraftStandardPrescriptionFieldLoaderRenerProps) => JSX.Element;
};

export const DraftStandardPrescriptionFieldLoader = (props: DraftStandardPrescriptionFieldLoaderProps): JSX.Element => {
   const patientEntitlementToMedicalServiceStatus = useGetPatientEntitlementToMedicalServiceStatus(props.visitId);

   const {
      data: medicinePrescription,
      loading: medicinePrescriptionLoading,
      error: medicinePrescriptionError,
   } = useCqrsGet<MedicinePrescription>(
      () => {
         const query = new MedicinePrescriptionQuery();
         query.patientId = props.patientId;
         query.medicineId = Slot.of(props.medicineId);
         query.ewusStatus = Choice.withUseDefaultIfNull(patientEntitlementToMedicalServiceStatus.ewusStatus);
         query.medicinePackageQuantity = Choice.of(props.medicinePackageQuantity);
         return query;
      },
      {
         initialData: props.initialData,
      }
   );

   if (medicinePrescriptionLoading) {
      return props.patientMedicine ? (
         <DraftPrescriptionCardSkeleton
            testId={props.index}
            heading={
               <MedicineHeading
                  medicine={props.patientMedicine.medicine}
                  reimbursements={props.patientMedicine.reimbursements}
                  visitDiagnoses={props.visitDiagnoses}
               />
            }
            subheading={
               <MedicinePackage
                  dose={props.patientMedicine.medicine.dose}
                  form={props.patientMedicine.medicine.form}
                  packageContent={props.patientMedicine.medicine.packageContent}
               />
            }
         />
      ) : (
         <LoaderSpinner />
      );
   }

   if (medicinePrescriptionError) {
      return (
         <Message
            testId={'fetchError'}
            type={MessageType.NEGATIVE}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            displayIndicator
         />
      );
   }

   return props.children({
      medicinePrescription: medicinePrescription,
   });
};
